import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { IoMdClose, IoMdInformationCircleOutline } from "react-icons/io";
import Card from "components/card";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import InputField from "components/fields/InputField";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import Select from "react-select";
import NotificationService from "../../../services/notification.service";
import EventService from "../../../services/events.service";
import CroppedImage from "../CroppedImage";
import imageCompression from "browser-image-compression";
import UserService from "services/user.service";
import { apiGet, apiPost, API_ENDPOINTS } from "../../../utils/apiUtils";
import tabCache from "utils/tabCache";

const getInitials = (name, email) => {
  if (name && typeof name === 'string') {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
    return parts[0][0].toUpperCase();
  }
  if (email && typeof email === 'string') return email.slice(0, 2).toUpperCase();
  return 'U';
};

const normalizeUser = (row) => {
  const account = row.accountDetails || row;
  const name = account.name || account.username || '';
  const email = account.email || '';
  return {
    id: row._id || row.id,
    name,
    email,
    profilepic: account.profilepic || row.profilepic,
    initials: getInitials(name, email),
  };
};

const extractSenderIdFromSettings = (senderUserId) => {
  if (!senderUserId) return null;
  if (typeof senderUserId === 'string') return senderUserId;
  const direct = senderUserId._id || senderUserId.id || senderUserId.userId;
  if (direct) return String(direct);
  const nested = senderUserId.accountDetails || senderUserId.account;
  if (nested && (nested._id || nested.id)) {
    return String(nested._id || nested.id);
  }
  return null;
};

const findUserInListBySenderId = (list, senderId) => {
  if (!senderId || !list?.length) return null;
  return (
    list.find((user) => {
      const uid = user?.id ?? user?._id;
      return uid != null && String(uid) === String(senderId);
    }) || null
  );
};

const UserMessageCard = ({ globalUsersList, globalLoading, globalHasMore, globalPage, globalLoadingMore, fetchMoreGlobalUsers, globalSettings, onRefreshSettings }) => {
  // Initialize from API settings (no localStorage persistence)
  const [selectedUser, setSelectedUser] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [tempSelectedUser, setTempSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isMessageEditing, setIsMessageEditing] = useState(false);
  const [message, setMessage] = useState('');
  const settingsRestoredRef = React.useRef(false);
  const welcomeMessageInputRef = React.useRef(null);
  const [localUsersList, setLocalUsersList] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const savedDataRef = React.useRef({ message: '', senderUserId: null, isAutoWelcomeEnabled: true });
  // When user turns toggle ON, snapshot user/message so Save stays disabled until they change something
  const toggleOnSnapshotRef = React.useRef(null);
  
  // Pagination states for local search
  const [localPage, setLocalPage] = useState(1);
  const [localHasMore, setLocalHasMore] = useState(false);
  const [localLoadingMore, setLocalLoadingMore] = useState(false);
  const [localTotalCount, setLocalTotalCount] = useState(0);
  const scrollContainerRef = React.useRef(null);

  // Use global users if not searching, otherwise use local search results
  const usersList = searchQuery ? localUsersList : globalUsersList;
  const usersLoading = searchQuery ? localLoading : globalLoading;
  const hasMore = searchQuery ? localHasMore : (globalHasMore || false);
  const loadingMore = searchQuery ? localLoadingMore : (globalLoadingMore || false);
  const currentPage = searchQuery ? localPage : (globalPage || 1);

  const fetchUsers = useCallback(async (search = '', page = 1, append = false) => {
    if (!search) {
      setLocalUsersList([]);
      setLocalPage(1);
      setLocalHasMore(false);
      return;
    }
    
    if (append) {
      setLocalLoadingMore(true);
    } else {
      setLocalLoading(true);
    }
    
    try {
      const body = {
        pagination: {
          page: page,
          pageSize: 20,
          projection: {},
          filters: {},
          sort: {},
        },
        searchtext: encodeURIComponent(search),
      };
      const response = await UserService.getAllUser(body);
      const data = response?.data?.data || [];
      const totalCount = response?.data?.totalCount || 0;
      const normalizedData = data.map(normalizeUser);
      
      if (append) {
        setLocalUsersList(prev => [...prev, ...normalizedData]);
      } else {
        setLocalUsersList(normalizedData);
      }
      
      const totalPages = Math.ceil(totalCount / 10);
      setLocalHasMore(page < totalPages);
      setLocalTotalCount(totalCount);
      setLocalPage(page);
    } catch (error) {
      Toast('error', error?.response?.data?.message || 'Failed to load users');
      if (!append) {
        setLocalUsersList([]);
      }
    } finally {
      setLocalLoading(false);
      setLocalLoadingMore(false);
    }
  }, []);

  const prevSearchRef = React.useRef(undefined);
  useEffect(() => {
    if (!popupOpen || !searchQuery) return;
    const isInitial = prevSearchRef.current === undefined;
    const isSearchChange = !isInitial && prevSearchRef.current !== searchQuery;
    prevSearchRef.current = searchQuery;
    if (!isSearchChange) return;

    const delay = 300;
    const timer = setTimeout(() => {
      setLocalPage(1);
      fetchUsers(searchQuery, 1, false);
    }, delay);
    return () => clearTimeout(timer);
  }, [popupOpen, searchQuery, fetchUsers]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Calculate if user is within 10 items from bottom
    // Assuming each item is approximately 60px height, 10 items = 600px
    const threshold = 600;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    if (distanceFromBottom < threshold && hasMore && !loadingMore && !usersLoading) {
      const nextPage = currentPage + 1;
      if (searchQuery) {
        fetchUsers(searchQuery, nextPage, true);
      } else if (fetchMoreGlobalUsers) {
        fetchMoreGlobalUsers(nextPage);
      }
    }
  }, [hasMore, loadingMore, usersLoading, currentPage, searchQuery, fetchUsers, fetchMoreGlobalUsers]);

  // Track last settings ID to detect changes
  const lastSettingsIdRef = React.useRef(null);
  const senderDetailFetchRef = React.useRef(null);

  // Restore toggle state from API settings whenever settings change
  useEffect(() => {
    if (globalSettings) {
      const settings = globalSettings;
      // Restore notifications enabled state from API settings
      // Check if the property exists and is a boolean (handles both true and false)
      if (settings.hasOwnProperty('isAutoWelcomeEnabled') && typeof settings.isAutoWelcomeEnabled === 'boolean') {
        const apiToggleValue = settings.isAutoWelcomeEnabled;
        // Always update from API value to ensure it matches what's saved
        setNotificationsEnabled(apiToggleValue);
      }
    }
  }, [globalSettings]);

  // Restore selected user + message from API settings (handles sender not on page 1 via getUserDetails)
  useEffect(() => {
    if (!globalSettings) return;

    const settings = globalSettings;
    const senderId = extractSenderIdFromSettings(settings.senderUserId);
    const settingsKey = senderId ?? '';

    if (lastSettingsIdRef.current !== settingsKey) {
      lastSettingsIdRef.current = settingsKey;
      settingsRestoredRef.current = false;
      senderDetailFetchRef.current = null;
    }

    if (settingsRestoredRef.current) return;

    if (typeof settings.welcomeMessage === 'string' && settings.welcomeMessage) {
      setMessage(settings.welcomeMessage);
    }

    if (!senderId) {
      setSelectedUser(null);
      savedDataRef.current = {
        message: settings.welcomeMessage || '',
        senderUserId: null,
        isAutoWelcomeEnabled: settings.isAutoWelcomeEnabled ?? true,
      };
      setHasUnsavedChanges(false);
      settingsRestoredRef.current = true;
      return;
    }

    if (settings.senderUserId && typeof settings.senderUserId === 'object') {
      const fromEmbed = normalizeUser(settings.senderUserId);
      const embedId = fromEmbed?.id ?? fromEmbed?._id;
      if (embedId && String(embedId) === String(senderId)) {
        setSelectedUser(fromEmbed);
        savedDataRef.current = {
          message: settings.welcomeMessage || '',
          senderUserId: senderId,
          isAutoWelcomeEnabled: settings.isAutoWelcomeEnabled ?? true,
        };
        setHasUnsavedChanges(false);
        settingsRestoredRef.current = true;
        return;
      }
    }

    const foundUser = findUserInListBySenderId(globalUsersList, senderId);
    if (foundUser) {
      setSelectedUser(foundUser);
      savedDataRef.current = {
        message: settings.welcomeMessage || '',
        senderUserId: senderId,
        isAutoWelcomeEnabled: settings.isAutoWelcomeEnabled ?? true,
      };
      setHasUnsavedChanges(false);
      settingsRestoredRef.current = true;
      return;
    }

    if (globalLoading && globalUsersList.length === 0) {
      return;
    }

    if (senderDetailFetchRef.current === senderId) {
      return;
    }
    senderDetailFetchRef.current = senderId;

    let cancelled = false;
    (async () => {
      try {
        const response = await UserService.getUserDetails({ id: senderId });
        const raw = response?.data?.data ?? response?.data;
        if (cancelled) return;
        if (raw && typeof raw === 'object') {
          setSelectedUser(normalizeUser(raw));
        } else {
          setSelectedUser({
            id: senderId,
            _id: senderId,
            name: '—',
            email: '',
            profilepic: undefined,
            initials: getInitials('—', ''),
          });
        }
        savedDataRef.current = {
          message: settings.welcomeMessage || '',
          senderUserId: senderId,
          isAutoWelcomeEnabled: settings.isAutoWelcomeEnabled ?? true,
        };
        setHasUnsavedChanges(false);
        settingsRestoredRef.current = true;
      } catch {
        senderDetailFetchRef.current = null;
        if (cancelled) return;
        setSelectedUser({
          id: senderId,
          _id: senderId,
          name: '—',
          email: '',
          profilepic: undefined,
          initials: getInitials('—', ''),
        });
        savedDataRef.current = {
          message: settings.welcomeMessage || '',
          senderUserId: senderId,
          isAutoWelcomeEnabled: settings.isAutoWelcomeEnabled ?? true,
        };
        setHasUnsavedChanges(false);
        settingsRestoredRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [globalSettings, globalUsersList, globalLoading]);

  const filteredUsers = usersList;
  // Only show tempSelectedUser in preview, don't fall back to selectedUser
  // This ensures cancel doesn't show any user and close icon clears properly
  const previewUser = tempSelectedUser;

  const handleSelectUser = (user) => {
    setTempSelectedUser(user);
    setSearchQuery('');
    setLocalUsersList([]);
    setLocalPage(1);
    setLocalHasMore(false);
    prevSearchRef.current = undefined;
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setTempSelectedUser(null);
    setSearchQuery('');
    setLocalUsersList([]);
    setLocalPage(1);
    setLocalHasMore(false);
    prevSearchRef.current = undefined;
  };

  const canSend = selectedUser && message.trim().length > 0 && notificationsEnabled;

  // When user just turned toggle ON: keep Save disabled until they select user + enter message (or change existing)
  const justTurnedToggleOn = notificationsEnabled && toggleOnSnapshotRef.current != null;
  const snapshot = toggleOnSnapshotRef.current;
  const currentSenderId = selectedUser?.id ?? selectedUser?._id ?? null;
  const currentMessage = (message || '').trim();
  const userOrMessageChangedSinceToggleOn =
    !justTurnedToggleOn ||
    (snapshot != null &&
      (String(currentSenderId) !== String(snapshot.senderUserId) || currentMessage !== snapshot.message));
  const requireUserAndMessageInteraction = justTurnedToggleOn && !userOrMessageChangedSinceToggleOn;

  // Track changes: enable Save when EITHER select user OR message is updated (API is only called when both are non-empty)
  // When user turns toggle ON, keep Save enabled until they save (toggle-on is an unsaved change)
  useEffect(() => {
    if (!notificationsEnabled) {
      setHasUnsavedChanges(false);
      return;
    }
    // User just turned toggle ON: enable Save so they can persist "toggle on" state
    if (toggleOnSnapshotRef.current != null) {
      setHasUnsavedChanges(true);
      return;
    }
    const hasSavedData = savedDataRef.current.senderUserId !== null || savedDataRef.current.message !== '';
    if (!hasSavedData) {
      // First time: enable Save when user or message is present (any one updated)
      setHasUnsavedChanges(Boolean(selectedUser) || message.trim().length > 0);
      return;
    }
    const currentSenderUserId = selectedUser?.id || selectedUser?._id || null;
    const savedSenderUserId = savedDataRef.current.senderUserId;
    const messageChanged = message.trim() !== savedDataRef.current.message;
    const senderChanged = String(currentSenderUserId) !== String(savedSenderUserId);
    // Enable Save when EITHER sender OR message is updated
    setHasUnsavedChanges(messageChanged || senderChanged);
  }, [message, selectedUser, notificationsEnabled]);

  // Warn user when they try to refresh/close with unsaved welcome message changes (browser default message)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // For tooltip: detect if only message or only sender was updated (when save again required)
  const currentMessageForTooltip = (message || '').trim();
  const currentSenderIdForTooltip = selectedUser?.id ?? selectedUser?._id ?? null;
  const messageChangedFromSaved = currentMessageForTooltip !== (savedDataRef.current?.message ?? '');
  const senderChangedFromSaved = String(currentSenderIdForTooltip) !== String(savedDataRef.current?.senderUserId ?? '');

  // Handle toggle change
  // When toggle is OFF → call API immediately
  // When toggle is ON → only update local state, wait for Save button to call API
  const handleToggleChange = async (newValue) => {
    // Update local state immediately for responsive UI
    setNotificationsEnabled(newValue);

    if (!newValue) {
      toggleOnSnapshotRef.current = null;
    } else {
      // User turned toggle ON: enable Save so they can save the "toggle on" state
      setHasUnsavedChanges(true);
      toggleOnSnapshotRef.current = {
        senderUserId: selectedUser?.id ?? selectedUser?._id ?? null,
        message: (message || '').trim(),
      };
    }

    // If toggle is being turned OFF, save to API immediately
    if (!newValue) {
      try {
        // Get sender user ID from selected user (if available)
        const senderUserId = selectedUser?.id || selectedUser?._id;

        // Save toggle OFF state to API
        const payload = {
          senderUserId: senderUserId || null,
          welcomeMessage: message.trim() || '',
          isAutoWelcomeEnabled: false,
        };

        // Use the settings API endpoint: POST /api/v1/admin/settings
        await apiPost(API_ENDPOINTS.SETTINGS.BASE, payload);

        // Refresh settings from API
        if (onRefreshSettings) {
          await onRefreshSettings();
        }
      } catch (error) {
        // Revert state on error
        setNotificationsEnabled(true);
        console.error('Failed to save toggle state to API', error);
        Toast('error', 'Failed to save toggle state');
      }
    }
    // If toggle is being turned ON, don't call API - wait for Save button
  };

  // When user selects in popup and clicks Save: call GET API.
  const handlePopupSave = async () => {
    const userToSave = tempSelectedUser || null;
    setSelectedUser(userToSave);
    handleClosePopup();

    // Call GET API when sender dropdown selection is saved.
    // To avoid overwriting the user's unsaved selection, we only apply
    // the response if API settings match the selected sender.
    if (!userToSave || !onRefreshSettings) return;

    try {
      const latest = await onRefreshSettings();
      if (!latest) return;

      const apiSenderId = latest.senderUserId
        ? (typeof latest.senderUserId === 'string'
            ? latest.senderUserId
            : latest.senderUserId._id || latest.senderUserId.id || latest.senderUserId.userId)
        : null;

      const selectedId = userToSave?.id || userToSave?._id || userToSave?.userId || null;

      if (apiSenderId && selectedId && String(apiSenderId) === String(selectedId)) {
        if (typeof latest.isAutoWelcomeEnabled === 'boolean') {
          setNotificationsEnabled(latest.isAutoWelcomeEnabled);
        }
        if (typeof latest.welcomeMessage === 'string') {
          setMessage(latest.welcomeMessage);
        }

        savedDataRef.current = {
          message: typeof latest.welcomeMessage === 'string' ? latest.welcomeMessage : '',
          senderUserId: apiSenderId,
          isAutoWelcomeEnabled: latest.isAutoWelcomeEnabled ?? true,
        };
        setHasUnsavedChanges(false);
        settingsRestoredRef.current = true;
      }
    } catch {
      // Ignore GET errors on selection; Save button flow still works.
    }
  };

  const handleSave = async () => {
    if (saving) return;

    // Only allow save when toggle is ON
    if (!notificationsEnabled) {
      Toast('error', 'Please turn on the toggle before saving.');
      return;
    }

    // Show helpful messages when Save is effectively "disabled"
    if (!selectedUser) {
      Toast('error', 'Please select a sender account before saving.');
      return;
    }

    if (!message.trim()) {
      Toast('error', 'Please enter a message before saving.');
      return;
    }

    setSaving(true);

    // Get sender user ID from selected user (from user listing API)
    const senderUserId = selectedUser?.id || selectedUser?._id;

    if (!senderUserId) {
      Toast('error', 'Please select a user from the user listing.');
      setSaving(false);
      return;
    }

    try {
      // Save message and settings to API
      const settingsPayload = {
        senderUserId: senderUserId,
        welcomeMessage: message.trim(),
        isAutoWelcomeEnabled: notificationsEnabled,
      };

      // Save settings to API
      await apiPost(API_ENDPOINTS.SETTINGS.BASE, settingsPayload);

     
      // This prevents the button from briefly showing as enabled
      savedDataRef.current = {
        message: message.trim(),
        senderUserId: senderUserId,
        isAutoWelcomeEnabled: notificationsEnabled,
      };
      setHasUnsavedChanges(false);
      toggleOnSnapshotRef.current = null;

     
      if (onRefreshSettings) {
        await onRefreshSettings();
      } else {
        // Fallback: call GET API directly if onRefreshSettings is not available
        await apiGet(API_ENDPOINTS.SETTINGS.BASE);
      }
      
      Toast('success', 'Welcome message settings saved successfully!');
    } catch (error) {
      console.error('Failed to save welcome message settings', error);
      Toast('error', error?.response?.data?.message || error?.message || 'Failed to save welcome message settings');
    } finally {
      // Exit edit mode
      setSaving(false);
      setIsEditing(false);
      setIsMessageEditing(false);
    }
  };





  return (
    <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
            height: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 10px;
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
          }
        `}
      </style>
      <Card extra="w-full h-full p-6 overflow-x-auto">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between pr-1">
            <h4 className="text-xl font-bold text-white">Welcome Message Automation</h4>
            <div className="flex items-center gap-2">
              <div className="relative group">
                <IoMdInformationCircleOutline className="h-5 w-5 text-[#8E8E8E] cursor-default" />
                <div className="pointer-events-none absolute -bottom-1 -left-10 z-10 w-max -translate-x-1/2 translate-y-full rounded-md bg-white/90 px-2 py-1 text-[12px] text-black opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
                  <span className="block">Enable or disable automatic welcome</span>
                  <span className="block"> messages for new sign-ups</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleToggleChange(!notificationsEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full border border-white transition-colors duration-200 ease-in-out focus:outline-none px-[2px] ${notificationsEnabled ? 'bg-[#F44C1D]' : 'bg-gray-600'
                  }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${notificationsEnabled ? 'translate-x-[20px]' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col items-center gap-4 w-full">
              <div className="relative group w-full">
                <button
                  type="button"
                  onClick={() => {
                    // Always initialize tempSelectedUser with selectedUser when opening popup
                    if (selectedUser) {
                      setTempSelectedUser(selectedUser);
                    } else {
                      setTempSelectedUser(null);
                    }
                    setPopupOpen(true);
                  }}
                  disabled={!notificationsEnabled}
                  className={`flex h-10 w-full flex-shrink-0 items-center justify-between rounded-xl bg-navy-900 px-4 text-left text-sm text-white outline-none transition-colors ${!notificationsEnabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-white/20'}`}
                >
                {selectedUser ? (
                  <div className="flex items-center gap-2 truncate">
                    {selectedUser.profilepic && typeof selectedUser.profilepic === 'string' ? (
                      <img src={selectedUser.profilepic.includes('http') ? selectedUser.profilepic : (process.env.REACT_APP_BASE_URL || '') + selectedUser.profilepic} alt="" className="h-6 w-6 rounded-full object-cover border border-white/10" />
                    ) : (
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#F44C1D]/30 text-[10px] font-semibold text-[#F44C1D]">{selectedUser.initials}</div>
                    )}
                    <span className="truncate text-sm text-white font-medium">{selectedUser.name || '—'}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 truncate">
                    <span className="truncate text-[#8E8E8E]">
                    Select  Sender Account
                    </span>
                  </div>
                )}
                {selectedUser ? (
                  <div
                    role="button"
                    onClick={(e) => { 
                      if (!notificationsEnabled) return;
                      e.stopPropagation(); 
                      setSelectedUser(null); 
                    }}
                    className={`transition-colors p-1 ${!notificationsEnabled ? 'opacity-50 cursor-not-allowed' : 'text-gray-400 hover:text-white'}`}
                  >
                    <IoMdClose size={16} />
                  </div>
                ) : (
                  <svg className={`h-4 w-4 flex-shrink-0 ${!notificationsEnabled ? 'text-gray-600' : 'text-white/60'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
                </button>
              </div>
            </div>
            <p className="mt-3 text-base text-gray-600">
            Automatically send a chat message to new users when they create an account.
             The message will be sent from the selected account below.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-base font-medium ml-1 text-white">Message Content</label>
            <div className="flex flex-col md:flex-row items-center gap-3">
              <div className="relative flex-1 w-full">
                <input
                  ref={welcomeMessageInputRef}
                  type="text"
                  value={message}
                  onChange={(e) => {
                    // Only allow changes when in edit mode (enabled by double-click)
                    if (isEditing || isMessageEditing) {
                      setMessage(e.target.value);
                    }
                  }}
                  onFocus={(e) => {
                    // Prevent focus unless in edit mode (double-clicked)
                    if (!isEditing && !isMessageEditing) {
                      e.target.blur();
                    }
                  }}
                  onDoubleClick={() => {
                    // Enable editing ONLY on double-click (even without user selected)
                    if (!notificationsEnabled) return;
                    if (!isEditing && !isMessageEditing) {
                      setIsMessageEditing(true);
                      setIsEditing(true);
                      setTimeout(() => welcomeMessageInputRef.current?.focus(), 0);
                    }
                  }}
                  onBlur={() => {
                    // Exit edit mode when field loses focus
                    setIsMessageEditing(false);
                    setIsEditing(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (isEditing || isMessageEditing) {
                        handleSave();
                      }
                    } else if (e.key === 'Escape') {
                      setIsEditing(false);
                      setIsMessageEditing(false);
                    }
                  }}
                  placeholder="Type message here..."
                  readOnly={(!isEditing && !isMessageEditing) || !notificationsEnabled}
                  disabled={!notificationsEnabled}
              
                  className={`flex h-10 w-full md:flex-1 items-center rounded-xl px-4 text-sm text-white bg-navy-900 placeholder:text-[#8E8E8E] outline-none transition-colors ${!notificationsEnabled ? 'opacity-50 cursor-not-allowed' : (isEditing || isMessageEditing) ? 'cursor-text' : 'cursor-pointer'}`}
                />
              </div>
              <div className="flex w-full md:w-auto items-center gap-2">
                <div className="relative group flex-1 md:w-24">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !notificationsEnabled || !hasUnsavedChanges}
                    className={`flex h-10 w-full items-center justify-center rounded-xl text-sm font-bold text-white transition-all shadow-lg active:scale-95 ${(saving || !notificationsEnabled || !hasUnsavedChanges) ? 'bg-gray-600 hover:bg-gray-600 cursor-not-allowed opacity-70' : 'bg-[#F44C1D] hover:bg-[#D43D15]'}`}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  {(saving || !notificationsEnabled || !hasUnsavedChanges) && (
                    <div className="pointer-events-none absolute bottom-full mb-2 sm:right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10">
                      <div className="rounded-md bg-white/90 px-1 py-1 text-[13px] text-black whitespace-nowrap shadow-lg">
                        {!notificationsEnabled
                          ? 'Turn on the toggle to save'
                          : 'Select a sender or type a message to enable Save'}
                      </div>
                    </div>
                  )}
                </div>
                {/* <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSend || saving}
                  className={`flex h-10 flex-1 md:w-24 items-center justify-center rounded-xl text-sm font-bold text-white transition-all shadow-lg active:scale-95 bg-[#F44C1D] hover:bg-[#D43D15] hover:scale-105 ${(!canSend || saving) ? 'cursor-not-allowed opacity-70' : ''}`}
                  title="Save"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button> */}
              </div>
            </div>
          </div>
        </div>

        {
          popupOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-[2px]">
              <div className="mx-3 flex w-full max-w-lg flex-col rounded-2xl bg-navy-800 shadow-2xl " onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="relative flex items-center justify-between border-b border-white/10 py-5 px-6">
                  <div className="flex items-center gap-3">
                    <p className="text-xl font-bold text-white">Select User</p>
                  </div>
                  <IoMdClose
                    className="cursor-pointer text-2xl text-gray-400 hover:text-white transition-colors duration-200"
                    onClick={handleClosePopup}
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-6">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search users..."
                      className="flex h-10 w-full items-center rounded-xl border border-white/10 bg-white/0 px-4 text-sm text-white  bg-navy-900 placeholder:!text-gray-600 outline-none focus:border-[#F44C1D]/50"
                    />
                    {previewUser && (
                      <div className="mt-4 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="flex items-center gap-3">
                          {previewUser.profilepic && typeof previewUser.profilepic === 'string' ? (
                            <img
                              src={previewUser.profilepic.includes('http') ? previewUser.profilepic : (process.env.REACT_APP_BASE_URL || '') + previewUser.profilepic}
                              alt=""
                              className="h-10 w-10 rounded-full object-cover border border-white/10"
                            />
                          ) : (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#F44C1D]/30 text-xs font-semibold text-[#F44C1D] border border-white/10">
                              {previewUser.initials}
                            </div>
                          )}
                          <div className="flex flex-col">
                            <p className="text-sm font-bold text-white">{previewUser.name}</p>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Selected User</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setTempSelectedUser(null)}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                        >
                          <IoMdClose size={18} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4 overflow-hidden">
                    {/* All Users */}
                    <div className="flex flex-col overflow-hidden">
                      <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500">All Users</p>
                      <div 
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        className="custom-scrollbar max-h-[300px] overflow-y-auto rounded-xl border border-white/10 bg-white/5 relative"
                      >
                        {usersLoading ? (
                          <div className="flex h-[300px] items-center justify-center">
                            <img src={Loader} alt="Loading..." className="h-10 w-10" />
                          </div>
                        ) : filteredUsers.length === 0 ? (
                          <div className="py-8 text-center text-sm text-gray-400">No users found</div>
                        ) : (
                          <>
                            <ul className="divide-y divide-white/10 w-full p-0 m-0">
                              {filteredUsers.map((user) => {
                                const userId = user.id || user._id;
                                const selectedId = tempSelectedUser?.id || tempSelectedUser?._id;
                                const isSelected = selectedId != null && String(selectedId) === String(userId);
                                const handleRowClick = (e) => {
                                  e.stopPropagation();
                                  if (isSelected) {
                                    setTempSelectedUser(null);
                                  } else {
                                    handleSelectUser(user);
                                  }
                                };
                                return (
                                  <li key={userId} className="w-full p-0 m-0">
                                    <div
                                      onClick={handleRowClick}
                                      role="button"
                                      tabIndex={0}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(e); } }}
                                      className="flex w-full cursor-pointer items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-white/5"
                                    >
                                      {user.profilepic && typeof user.profilepic === 'string' ? (
                                        <img
                                          src={user.profilepic.includes('http') ? user.profilepic : (process.env.REACT_APP_BASE_URL || '') + user.profilepic}
                                          alt=""
                                          className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                                          onError={(e) => { e.target.style.display = 'none'; if (e.target.nextElementSibling) e.target.nextElementSibling.classList.remove('!hidden'); }}
                                        />
                                      ) : null}
                                      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#F44C1D]/30 text-xs font-semibold text-[#F44C1D] ${user.profilepic ? '!hidden' : ''}`}>
                                        {user.initials}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-white">{user.name || '—'}</p>
                                      </div>
                                      <div
                                        onClick={(e) => { e.stopPropagation(); handleRowClick(e); }}
                                        className={`flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-md border-[1.5px] transition-all duration-200 pointer-events-auto ${isSelected
                                          ? 'bg-[#F44C1D] border-[#F44C1D] shadow-[0_0_10px_rgba(244,76,29,0.3)]'
                                          : 'border-white/20 bg-transparent hover:border-[#F44C1D]/50'
                                          }`}
                                      >
                                        {isSelected && (
                                          <svg
                                            className="h-3 w-3 text-white pointer-events-none"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={3}
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M5 13l4 4L19 7"
                                            />
                                          </svg>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                            {loadingMore && (
                              <div className="flex items-center justify-center py-4">
                                <img src={Loader} alt="Loading more..." className="h-8 w-8" />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end pt-6">
                    <button
                      type="button"
                      onClick={handlePopupSave}
                      className="flex h-8 w-20 items-center justify-center rounded-lg bg-[#F44C1D] text-xs font-bold text-white transition-all hover:bg-[#D43D15] shadow-lg active:scale-95"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

      </Card >
    </div >
  );
};

const MultiUserMessageCard = ({ globalUsersList, globalLoading, globalSettings, globalHasMore, globalPage, globalLoadingMore, fetchMoreGlobalUsers, onRefreshSettings }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [message, setMessage] = useState('');
  const [localUsersList, setLocalUsersList] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [senderId, setSenderId] = useState(null);
  const [isMessageEditing, setIsMessageEditing] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [senderPopupOpen, setSenderPopupOpen] = useState(false);
  const [tempSelectedSender, setTempSelectedSender] = useState(null);
  
  // Pagination states for local search
  const [localPage, setLocalPage] = useState(1);
  const [localHasMore, setLocalHasMore] = useState(false);
  const [localLoadingMore, setLocalLoadingMore] = useState(false);
  const [localTotalCount, setLocalTotalCount] = useState(0);
  const scrollContainerRef = React.useRef(null);
  const messageInputRef = React.useRef(null);

  // Use global data
  const usersList = searchQuery ? localUsersList : globalUsersList;
  const usersLoading = searchQuery ? localLoading : globalLoading;
  const hasMore = searchQuery ? localHasMore : (globalHasMore || false);
  const loadingMore = searchQuery ? localLoadingMore : (globalLoadingMore || false);
  const currentPage = searchQuery ? localPage : (globalPage || 1);
  // Only show tempSelectedSender in preview, don't fall back to selectedSender
  // This ensures cancel doesn't show any user and close icon clears properly
  const previewSender = tempSelectedSender;

  const fetchUsers = useCallback(async (search = '', page = 1, append = false) => {
    if (!search) {
      setLocalUsersList([]);
      setLocalPage(1);
      setLocalHasMore(false);
      return;
    }
    
    if (append) {
      setLocalLoadingMore(true);
    } else {
      setLocalLoading(true);
    }
    
    try {
      const body = {
        pagination: {
          page: page,
          pageSize: 20,
          projection: {},
          filters: {},
          sort: {},
        },
        searchtext: encodeURIComponent(search),
      };
      const response = await UserService.getAllUser(body);
      const data = response?.data?.data || [];
      const totalCount = response?.data?.totalCount || 0;
      const normalizedData = data.map(normalizeUser);
      
      if (append) {
        setLocalUsersList(prev => [...prev, ...normalizedData]);
      } else {
        setLocalUsersList(normalizedData);
      }
      
      const totalPages = Math.ceil(totalCount / 10);
      setLocalHasMore(page < totalPages);
      setLocalTotalCount(totalCount);
      setLocalPage(page);
    } catch (error) {
      Toast('error', error?.response?.data?.message || 'Failed to load users');
      if (!append) {
        setLocalUsersList([]);
      }
    } finally {
      setLocalLoading(false);
      setLocalLoadingMore(false);
    }
  }, []);

  const prevSearchRef = React.useRef(undefined);
  useEffect(() => {
    if ((!popupOpen && !senderPopupOpen) || !searchQuery) return;
    const isInitial = prevSearchRef.current === undefined;
    const isSearchChange = !isInitial && prevSearchRef.current !== searchQuery;
    prevSearchRef.current = searchQuery;
    if (!isSearchChange) return;

    const delay = 300;
    const timer = setTimeout(() => {
      setLocalPage(1);
      fetchUsers(searchQuery, 1, false);
    }, delay);
    return () => clearTimeout(timer);
  }, [popupOpen, senderPopupOpen, searchQuery, fetchUsers]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Calculate if user is within 10 items from bottom
    // Assuming each item is approximately 60px height, 10 items = 600px
    const threshold = 600;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    if (distanceFromBottom < threshold && hasMore && !loadingMore && !usersLoading) {
      const nextPage = currentPage + 1;
      if (searchQuery) {
        fetchUsers(searchQuery, nextPage, true);
      } else if (fetchMoreGlobalUsers) {
        fetchMoreGlobalUsers(nextPage);
      }
    }
  }, [hasMore, loadingMore, usersLoading, currentPage, searchQuery, fetchUsers, fetchMoreGlobalUsers]);

  // Apply settings from parent (only for notifications enabled, NOT for message - message resets on refresh)
  useEffect(() => {
    if (globalSettings) {
      const settings = globalSettings;
      // Don't load senderUserId from settings - one-time chat message sender is independent
      // and should reset to null on every page refresh (doesn't persist)
      // This prevents interference with welcome message sender selection
      // Message also resets on refresh - don't restore from settings
      if (typeof settings.isAutoWelcomeEnabled === 'boolean') {
        setNotificationsEnabled(settings.isAutoWelcomeEnabled);
      }
    }
  }, [globalSettings]);

  // Normalize user id for comparison (API may return id or _id)
  const getUserId = (u) => u?.id ?? u?._id;
  const isSameUser = (u, v) => {
    const a = getUserId(u);
    const b = getUserId(v);
    return a != null && b != null && String(a) === String(b);
  };

  // For Select Users popup left list: show all users except sender; selected users stay in list and show orange checkmark
  const allUsersForSelectPopup = usersList.filter(user => 
    !selectedSender || getUserId(selectedSender) !== getUserId(user)
  );
  // For Select Sender popup: filter out selected users so they don't appear in sender list
  const filteredUsers = usersList.filter(user => 
    (!selectedSender || getUserId(selectedSender) !== getUserId(user)) && 
    !selectedUsers.some(selectedUser => isSameUser(selectedUser, user))
  );

  const toggleUserSelection = (user) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => isSameUser(u, user));
      if (isSelected) {
        return prev.filter((u) => !isSameUser(u, user));
      } else {
        if (prev.length >= 20) {
          Toast('warning', 'Maximum 20 users can be selected');
          return prev;
        }
        return [...prev, user];
      }
    });
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSearchQuery('');
    setLocalUsersList([]);
    setLocalPage(1);
    setLocalHasMore(false);
    prevSearchRef.current = undefined;
  };

  const handleCloseSenderPopup = () => {
    setSenderPopupOpen(false);
    setTempSelectedSender(null);
    setSearchQuery('');
    setLocalUsersList([]);
    setLocalPage(1);
    setLocalHasMore(false);
    prevSearchRef.current = undefined;
  };

  const canSend = selectedUsers.length > 0 && selectedSender && message.trim().length > 0;

  // Save sender account selection to API (similar to welcome message card)
  // Save sender selection for one-time chat message (local state only, no API, no persistence)
  // This ensures it doesn't interfere with welcome message sender selection
  // and resets to null on every page refresh
  const saveSenderSelection = async (sender) => {
    // Update local state only - no API call, no localStorage persistence
    // This keeps it completely independent from welcome message settings
    if (sender) {
      const senderUserId = sender?.id || sender?._id;
      setSelectedSender(sender);
      setSenderId(senderUserId || null);
    } else {
      setSelectedSender(null);
      setSenderId(null);
    }
  };

  const handleSave = async () => {
    if (!canSend || saving) return;
    if (!senderId) {
      Toast('error', 'Sender is not configured. Please set sender in settings first.');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        senderId: typeof senderId === 'string' ? senderId : senderId?._id || senderId?.id || senderId?.userId,
        content: message,
        receiverUserId: selectedUsers.map((user) => user.id || user._id),
      };

      const response = await apiPost(API_ENDPOINTS.MANUAL_MESSAGES.SEND, payload);

      if (response?.data) {
        Toast('success', 'Message sent successfully!');
      } else {
        Toast('success', 'Message saved successfully!');
      }
      setSelectedUsers([]);
      setSelectedSender(null);
      setSenderId(null);
      setMessage('');
    } catch (error) {
      Toast('error', error?.response?.data?.message || 'Failed to save message settings');
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
            height: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 10px;
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
          }
        `}
      </style>
      <Card extra="w-full h-full p-6 overflow-x-auto">
        <div className="flex flex-col -gap-1">
          <h4 className="text-xl font-bold text-white">One-Time Chat Message </h4>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between pr-1">
              {/* <label className="text-base font-medium ml-1 text-white">Select Users</label> */}
              {/* <button
                type="button"
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full border border-white transition-colors duration-200 ease-in-out focus:outline-none px-0.5 ${notificationsEnabled ? 'bg-[#F44C1D]' : 'bg-gray-700'
                  }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${notificationsEnabled ? 'translate-x-[22px]' : 'translate-x-0'
                    }`}
                />
              </button> */}
            </div>
            <div className="flex flex-col gap-3 w-full">
              <div className="grid w-full grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex flex-col w-full gap-1">
                  <label className="text-sm font-medium ml-1 text-white">Select Users</label>
                  <button
                    type="button"
                    onClick={() => setPopupOpen(true)}
                    className="flex h-10 w-full items-center justify-between rounded-xl bg-navy-900 px-4 text-left text-sm text-white outline-none transition-colors hover:border-white/20"
                  >
                    <div className="flex items-center gap-2 truncate">
                      <span className="truncate text-[#8E8E8E]">
                        {selectedUsers.length > 0 ? `Selected Users (${selectedUsers.length})` : 'Select Users'}
                      </span>
                    </div>
                    <svg className="h-4 w-4 flex-shrink-0 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                <div className="flex flex-col w-full gap-1">
                  <label className="text-sm font-medium ml-1 text-white">Select Sender Account</label>
                  <button
                    type="button"
                    onClick={() => {
                      // Always initialize tempSelectedSender with selectedSender when opening popup
                      if (selectedSender) {
                        setTempSelectedSender(selectedSender);
                      } else {
                        setTempSelectedSender(null);
                      }
                      setSenderPopupOpen(true);
                    }}
                    className="flex h-10 w-full items-center justify-between rounded-xl bg-navy-900 px-4 text-left text-sm text-white outline-none transition-colors hover:border-white/20"
                  >
                    {selectedSender ? (
                      <div className="flex items-center gap-2 truncate">
                        {selectedSender.profilepic && typeof selectedSender.profilepic === 'string' ? (
                          <img
                            src={selectedSender.profilepic.includes('http') ? selectedSender.profilepic : (process.env.REACT_APP_BASE_URL || '') + selectedSender.profilepic}
                            alt=""
                            className="h-6 w-6 rounded-full object-cover border border-white/10"
                          />
                        ) : (
                          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#F44C1D]/30 text-[10px] font-semibold text-[#F44C1D]">
                            {selectedSender.initials}
                          </div>
                        )}
                        <span className="truncate text-sm text-white font-medium">
                          {selectedSender.name || '—'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 truncate">
                        <span className="truncate text-[#8E8E8E]">
                          Select Sender Account
                        </span>
                      </div>
                    )}
                    {selectedSender ? (
                      <div
                        role="button"
                        onClick={async (e) => { 
                          e.stopPropagation(); 
                          setSelectedSender(null);
                          setSenderId(null);
                          // Also clear from API
                          await saveSenderSelection(null);
                        }}
                        className="text-gray-400 hover:text-white transition-colors p-1"
                      >
                        <IoMdClose size={16} />
                      </div>
                    ) : (
                      <svg className="h-4 w-4 flex-shrink-0 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="relative w-full md:w-48">
                  {/* <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex h-10 w-full items-center justify-between rounded-xl  bg-navy-900 placeholder:!text-gray-600 px-4 text-left text-sm text-white outline-none transition-colors hover:border-white/20"
                  >
                    <span className="truncate text-[#8E8E8E]">
                      View Selected Users
                    </span>
                    <svg className={`h-4 w-4 flex-shrink-0 text-white/60 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button> */}

                  {isDropdownOpen && (
                    <div className="absolute right-0 top-full z-50 mt-2 w-full md:w-64 rounded-xl border border-white/10 bg-navy-800 p-2 shadow-2xl backdrop-blur-xl">
                      <div className="custom-scrollbar max-h-36 overflow-y-auto">
                        {selectedUsers.length === 0 ? (
                          <div className="p-4 text-center text-sm text-gray-400">No users selected</div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {selectedUsers.map((user) => (
                              <div key={getUserId(user) ?? user.id ?? user._id} className="flex items-center justify-between rounded-lg p-2 hover:bg-white/5 transition-colors">
                                <div className="flex items-center gap-2 overflow-hidden">
                                  {user.profilepic && typeof user.profilepic === 'string' ? (
                                    <img src={user.profilepic.includes('http') ? user.profilepic : (process.env.REACT_APP_BASE_URL || '') + user.profilepic} alt="" className="h-6 w-6 rounded-full object-cover border border-white/10" />
                                  ) : (
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F44C1D]/30 text-[8px] font-semibold text-[#F44C1D]">{user.initials}</div>
                                  )}
                                  <span className="truncate text-xs text-white">{user.name || '—'}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleUserSelection(user); }}
                                  className="text-gray-400 hover:text-white transition-colors"
                                >
                                  <IoMdClose size={14} />

                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <p className="mt-1 mb-3 text-base text-gray-600">
            Send a one-time chat message to specific users from a selected account. 
            Perfect for personal updates, reminders, or targeted communication.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-base font-medium ml-1 text-white">Message Content</label>
              <div className="relative group">
                {/* <IoMdInformationCircleOutline className="text-[#8E8E8E] hover:text-white transition-colors cursor-help" size={20} /> */}
                {/* <div className="pointer-events-none absolute bottom-full mb-2 -left-24 sm:left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10">
                  <div className="rounded-md bg-white/90 px-2 py-1 text-[13px] text-black whitespace-nowrap shadow-lg">
                    Double-click the input field to edit message
                  </div>
                </div> */}
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="relative w-full">
                <input
                  ref={messageInputRef}
                  type="text"
                  value={message}
                  readOnly={!isMessageEditing}
                  onChange={(e) => setMessage(e.target.value)}
                  onDoubleClick={() => {
                    if (!isMessageEditing) {
                      setIsMessageEditing(true);
                      setTimeout(() => messageInputRef.current?.focus(), 0);
                    }
                  }}
                  onBlur={() => {
                    setIsMessageEditing(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setIsMessageEditing(false);
                    } else if (e.key === 'Escape') {
                      setIsMessageEditing(false);
                    }
                  }}
                  placeholder="Type message here..."
                  className={`flex h-10 w-full items-center rounded-xl px-4 text-sm text-white bg-navy-900 placeholder:!text-gray-600 outline-none transition-colors ${isMessageEditing ? 'cursor-text ' : 'cursor-pointer'}`}
                />
              </div>
              <div className="flex w-full md:w-auto items-center gap-2">
                <div className="relative group flex-1 md:w-28">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canSend || saving}
                    className={`flex h-10 w-full items-center justify-center rounded-xl text-sm font-bold text-white transition-all shadow-lg active:scale-95 ${
                      (!canSend || saving)
                        ? 'bg-gray-600 hover:bg-gray-600 cursor-not-allowed opacity-70'
                        : 'bg-[#F44C1D] hover:bg-[#D43D15] '
                    }`}
                  >
                    {saving ? 'Sending...' : 'Send Message'}
                  </button>
                  {!saving && !canSend && (
                    <div className={`pointer-events-none absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10 ${
                      (!message.trim() && selectedSender && selectedUsers.length > 0) ||
                      (selectedUsers.length === 0 && selectedSender)
                        ? 'right-0'
                        : 'left-0 transform -translate-x-4 sm:-translate-x-48'
                    }`}>
                       <div className="rounded-md bg-white/90 px-1 py-1 text-[13px] text-black whitespace-nowrap shadow-lg">
                        {!selectedSender && selectedUsers.length === 0 && !message.trim()
                          ? "Select sender account, users, and type a message"
                          : !selectedSender && selectedUsers.length === 0
                          ? "Select sender account and users to send message"
                          : !selectedSender && !message.trim()
                          ? "Select sender account and type a message"
                          : selectedUsers.length === 0 && !message.trim()
                          ? "Select users and type a message"
                          : !selectedSender
                          ? "Select sender account to send message"
                          : selectedUsers.length === 0
                          ? "Select users to send message"
                          : !message.trim()
                          ? "Type a message to send"
                          : ""}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          {/* </div> */}
        </div>

        {
          popupOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-[2px]">
              <div className="mx-3 flex w-full max-w-2xl flex-col rounded-2xl bg-navy-800 shadow-2xl " onClick={(e) => e.stopPropagation()}>
                <div className="relative flex items-center justify-between border-b border-white/10 py-5 px-6">
                  <div className="flex items-center gap-3">
                    <p className="text-xl font-bold text-white">Select Users</p>
                  </div>
                  <IoMdClose
                    className="cursor-pointer text-2xl text-gray-400 hover:text-white transition-colors duration-200"
                    onClick={handleClosePopup}
                  />
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search users..."
                      className="flex h-10 w-full items-center rounded-xl border border-white/10 bg-white/0 px-4 text-sm text-white  bg-navy-900 placeholder:rgba(30, 30, 32, 1) outline-none focus:border-[#F44C1D]/50"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 overflow-hidden md:grid-cols-3">
                    {/* Left Side: All Users */}
                    <div className="flex flex-col overflow-hidden md:col-span-2">
                      <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500">All Users</p>
                      <div 
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        className="custom-scrollbar max-h-[350px] overflow-y-auto rounded-xl border border-white/10 bg-white/5 relative"
                      >
                        {usersLoading ? (
                          <div className="flex h-[350px] items-center justify-center">
                            <img src={Loader} alt="Loading..." className="h-10 w-10" />
                          </div>
                        ) : allUsersForSelectPopup.length === 0 ? (
                          <div className="py-8 text-center text-sm text-gray-400">No users found</div>
                        ) : (
                          <>
                            <ul className="divide-y divide-white/10 w-full p-0 m-0">
                              {allUsersForSelectPopup.map((user) => {
                                const isSelected = selectedUsers.some((u) => isSameUser(u, user));
                                const userId = getUserId(user);
                                return (
                                  <li key={userId ?? user.id ?? user._id} className="w-full p-0 m-0">
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleUserSelection(user);
                                      }}
                                      className="flex w-full cursor-pointer items-center gap-3 px-2 py-2 text-left transition-colors hover:bg-white/5"
                                    >
                                      {user.profilepic && typeof user.profilepic === 'string' ? (
                                        <img
                                          src={user.profilepic.includes('http') ? user.profilepic : (process.env.REACT_APP_BASE_URL || '') + user.profilepic}
                                          alt=""
                                          className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                                          onError={(e) => { e.target.style.display = 'none'; if (e.target.nextElementSibling) e.target.nextElementSibling.classList.remove('!hidden'); }}
                                        />
                                      ) : null}
                                      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#F44C1D]/30 text-xs font-semibold text-[#F44C1D] ${user.profilepic ? '!hidden' : ''}`}>
                                        {user.initials}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-white">{user.name || '—'}</p>
                                      </div>
                                      <div
                                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-md border-[1.5px] transition-all duration-200 ${isSelected
                                          ? 'bg-[#F44C1D] border-[#F44C1D] shadow-[0_0_10px_rgba(244,76,29,0.3)]'
                                          : 'border-white/20 bg-transparent hover:border-[#F44C1D]/50'
                                          }`}
                                      >
                                        {isSelected && (
                                          <svg
                                            className="h-3 w-3 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={3}
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M5 13l4 4L19 7"
                                            />
                                          </svg>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                            {loadingMore && (
                              <div className="flex items-center justify-center py-4">
                                <img src={Loader} alt="Loading more..." className="h-8 w-8" />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Right Side: Selected Users */}
                    <div className="flex flex-col overflow-hidden border-t md:border-t-0 md:border-l border-white/10 pt-4 md:pt-0 md:pl-4 md:col-span-1">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Selected ({selectedUsers.length})</p>
                        {selectedUsers.length > 0 && (
                          <button
                            onClick={() => setSelectedUsers([])}
                            className="text-[12px] text-gray-500 hover:text-white transition-colors"
                          >
                            Clear all
                          </button>
                        )}
                      </div>

                      <div className="flex min-h-[200px] flex-1 flex-col">
                        {selectedUsers.length === 0 ? (
                          <div className="flex flex-1 items-center justify-center">
                            <span className="text-sm text-gray-400">No selected users</span>
                          </div>
                        ) : (
                          <div className="custom-scrollbar max-h-[350px] overflow-y-auto rounded-xl border border-white/10 bg-white/5">
                          <ul className="divide-y divide-white/10 w-full p-0 m-0">
                            {selectedUsers.map((user) => (
                              <li key={getUserId(user) ?? user.id ?? user._id} className="w-full p-0 m-0">
                                <div className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-white/5">
                                  {user.profilepic && typeof user.profilepic === 'string' ? (
                                    <img
                                      src={user.profilepic.includes('http') ? user.profilepic : (process.env.REACT_APP_BASE_URL || '') + user.profilepic}
                                      alt=""
                                      className="h-7 w-7 flex-shrink-0 rounded-full object-cover border border-white/10"
                                    />
                                  ) : (
                                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#F44C1D]/30 text-[9px] font-semibold text-[#F44C1D] border border-white/10">
                                      {user.initials}
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-xs font-medium text-white">{user.name || '—'}</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => toggleUserSelection(user)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                  >
                                    <IoMdClose className="text-base" />
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end p-6 pt-0">
                  <button
                    type="button"
                    onClick={handleClosePopup}
                    className="flex h-8 w-20 items-center justify-center rounded-lg bg-[#F44C1D] text-xs font-bold text-white transition-all hover:bg-[#D43D15] shadow-lg active:scale-95"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )
        }

        {
          senderPopupOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-[2px]">
              <div className="mx-3 flex w-full max-w-lg flex-col rounded-2xl bg-navy-800 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="relative flex items-center justify-between border-b border-white/10 py-5 px-6">
                  <div className="flex items-center gap-3">
                    <p className="text-xl font-bold text-white">Select Sender Account</p>
                  </div>
                  <IoMdClose
                    className="cursor-pointer text-2xl text-gray-400 hover:text-white transition-colors duration-200"
                    onClick={handleCloseSenderPopup}
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-6">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search users..."
                      className="flex h-10 w-full items-center rounded-xl border border-white/10 bg-white/0 px-4 text-sm text-white bg-navy-900 placeholder:!text-gray-600 outline-none focus:border-[#F44C1D]/50"
                    />
                    {previewSender && (
                      <div className="mt-4 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="flex items-center gap-3">
                          {previewSender.profilepic && typeof previewSender.profilepic === 'string' ? (
                            <img
                              src={previewSender.profilepic.includes('http') ? previewSender.profilepic : (process.env.REACT_APP_BASE_URL || '') + previewSender.profilepic}
                              alt=""
                              className="h-10 w-10 rounded-full object-cover border border-white/10"
                            />
                          ) : (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#F44C1D]/30 text-xs font-semibold text-[#F44C1D] border border-white/10">
                              {previewSender.initials}
                            </div>
                          )}
                          <div className="flex flex-col">
                            <p className="text-sm font-bold text-white">{previewSender.name}</p>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Selected Sender</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setTempSelectedSender(null)}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                        >
                          <IoMdClose size={18} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4 overflow-hidden">
                    {/* All Users */}
                    <div className="flex flex-col overflow-hidden">
                      <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500">All Users</p>
                      <div
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        className="custom-scrollbar max-h-[300px] overflow-y-auto rounded-xl border border-white/10 bg-white/5 relative"
                      >
                        {usersLoading ? (
                          <div className="flex h-[300px] items-center justify-center">
                            <img src={Loader} alt="Loading..." className="h-10 w-10" />
                          </div>
                        ) : filteredUsers.length === 0 ? (
                          <div className="py-8 text-center text-sm text-gray-400">No users found</div>
                        ) : (
                          <>
                            <ul className="divide-y divide-white/10 w-full p-0 m-0">
                              {filteredUsers.map((user) => {
                                const isSelected = tempSelectedSender?.id === user.id;
                                return (
                                  <li key={user.id} className="w-full p-0 m-0">
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isSelected) {
                                          setTempSelectedSender(null);
                                        } else {
                                          setTempSelectedSender(user);
                                        }
                                      }}
                                      className="flex w-full cursor-pointer items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-white/5"
                                    >
                                      {user.profilepic && typeof user.profilepic === 'string' ? (
                                        <img
                                          src={user.profilepic.includes('http') ? user.profilepic : (process.env.REACT_APP_BASE_URL || '') + user.profilepic}
                                          alt=""
                                          className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            if (e.target.nextElementSibling) e.target.nextElementSibling.classList.remove('!hidden');
                                          }}
                                        />
                                      ) : null}
                                      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#F44C1D]/30 text-xs font-semibold text-[#F44C1D] ${user.profilepic ? '!hidden' : ''}`}>
                                        {user.initials}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-white">{user.name || '—'}</p>
                                      </div>
                                      <div
                                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-md border-[1.5px] transition-all duration-200 ${
                                          isSelected
                                            ? 'bg-[#F44C1D] border-[#F44C1D] shadow-[0_0_10px_rgba(244,76,29,0.3)]'
                                            : 'border-white/20 bg-transparent hover:border-[#F44C1D]/50'
                                        }`}
                                      >
                                        {isSelected && (
                                          <svg
                                            className="h-3 w-3 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={3}
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M5 13l4 4L19 7"
                                            />
                                          </svg>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                            {loadingMore && (
                              <div className="flex items-center justify-center py-4">
                                <img src={Loader} alt="Loading more..." className="h-8 w-8" />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-6">
                    <button
                      type="button"
                      onClick={async () => {
                        // Set the selected sender first
                        const senderToSave = tempSelectedSender || null;
                        setSelectedSender(senderToSave);
                        setSenderId(
                          senderToSave
                            ? senderToSave.id || senderToSave._id || senderToSave.userId
                            : null
                        );
                        handleCloseSenderPopup();
                        
                        // Save sender selection to API so it persists on refresh (can be null to clear)
                        await saveSenderSelection(senderToSave);
                      }}
                      className="flex h-8 w-20 items-center justify-center rounded-lg bg-[#F44C1D] text-xs font-bold text-white transition-all hover:bg-[#D43D15] shadow-lg active:scale-95"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      </Card >
    </div >
  );
};

const Notification = () => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [initialImage, setInitialImage] = useState("");
  const [croppedImage, setCroppedImage] = useState("");
  const [selectedScale, setSelectedScale] = useState("16:9");
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Global state for users and settings
  const [globalUsersList, setGlobalUsersList] = useState(() => tabCache.notificationUsers.data || []);
  const [globalUsersLoading, setGlobalUsersLoading] = useState(false);
  const [globalUsersLoadingMore, setGlobalUsersLoadingMore] = useState(false);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [globalPage, setGlobalPage] = useState(1);
  const [globalHasMore, setGlobalHasMore] = useState(false);
  const [globalTotalCount, setGlobalTotalCount] = useState(0);

  // Remove persisted caches (requested)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem('user_message_cache');
      localStorage.removeItem('user_selected_user_cache');
    } catch {
      // ignore
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    // Only fetch if not already loaded in the current tab session across both APIs
    // However, if one is missing, we fetch what's needed.
    const shouldFetchUsers = tabCache.notificationUsers.firstLoad || !tabCache.notificationUsers.data;

    setGlobalUsersLoading(true);
    try {
      const promises = [apiGet(API_ENDPOINTS.SETTINGS.BASE)];
      if (shouldFetchUsers) {
        const userBody = {
          pagination: { page: 1, pageSize: 20, projection: {}, filters: {}, sort: {} },
          searchtext: '',
        };
        promises.push(UserService.getAllUser(userBody));
      }

      const results = await Promise.allSettled(promises);

      // First promise is always settings
      if (results[0].status === 'fulfilled') {
        const raw = results[0].value?.data || {};
        const settings = raw?.data || raw;
        setGlobalSettings(settings);
      }

      // Second promise (if exists) is users
      if (shouldFetchUsers && results[1]?.status === 'fulfilled') {
        const data = results[1].value?.data?.data || [];
        const totalCount = results[1].value?.data?.totalCount || 0;
        const normalizedData = data.map(normalizeUser);
        setGlobalUsersList(normalizedData);
        setGlobalTotalCount(totalCount);
        const totalPages = Math.ceil(totalCount / 10);
        setGlobalHasMore(1 < totalPages);
        setGlobalPage(1);
        tabCache.notificationUsers.data = normalizedData;
        tabCache.notificationUsers.firstLoad = false;
      }
    } catch (error) {
      console.error('Failed to load initial notification data', error);
    } finally {
      setGlobalUsersLoading(false);
    }
  }, []);

  const fetchMoreGlobalUsers = useCallback(async (page) => {
    if (globalUsersLoadingMore || globalUsersLoading) return;
    
    setGlobalUsersLoadingMore(true);
    try {
      const userBody = {
        pagination: { page: page, pageSize: 20, projection: {}, filters: {}, sort: {} },
        searchtext: '',
      };
      const response = await UserService.getAllUser(userBody);
      const data = response?.data?.data || [];
      const totalCount = response?.data?.totalCount || 0;
      const normalizedData = data.map(normalizeUser);
      
      setGlobalUsersList(prev => [...prev, ...normalizedData]);
      setGlobalTotalCount(totalCount);
      const totalPages = Math.ceil(totalCount / 10);
      setGlobalHasMore(page < totalPages);
      setGlobalPage(page);
    } catch (error) {
      Toast('error', error?.response?.data?.message || 'Failed to load more users');
    } finally {
      setGlobalUsersLoadingMore(false);
    }
  }, [globalUsersLoadingMore, globalUsersLoading]);

  // Function to refresh settings from API
  const refreshSettings = useCallback(async () => {
    try {
      const response = await apiGet(API_ENDPOINTS.SETTINGS.BASE);
      const raw = response?.data || {};
      const settings = raw?.data || raw;
      setGlobalSettings(settings);
      return settings;
    } catch (error) {
      console.error('Failed to refresh settings', error);
      Toast('error', 'Failed to refresh settings');
      return null;
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const topicOptions = [
    { value: 'all', label: 'All' },
    { value: 'new_features', label: 'New Features' },
    { value: 'offer', label: 'Offer' },
    { value: 'daily_update', label: 'Daily Update' },
    { value: 'near_event', label: 'Near Event' },
  ];


  const formik = useFormik({
    initialValues: {
      topic: '',
      title: '',
      body: '',
      image: '',
    },
    validationSchema: Yup.object({
      topic: Yup.string().required('Topic is required'),
      title: Yup.string().required('Title is required'),
      body: Yup.string().required('Body is required'),
      image: Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = {
          topic: values.topic,
          title: values.title,
          body: values.body
        };

        // Only add image if it exists
        if (values.image) {
          payload.image = values.image;
        }

        const response = await NotificationService.sendNotification(payload);

        if (response.data) {
          Toast('success', 'Notification sent successfully!');
          handleClose();
        }
      } catch (error) {
        Toast('error', error.response?.data?.message || 'Failed to send notification. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
    setSelectedImage("");
    setInitialImage("");
    setCroppedImage("");
  };

  const handleTopicChange = (selectedOption) => {
    formik.setFieldValue('topic', selectedOption.value);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    setInitialImage(file);
    e.target.value = "";
  };

  const handleAspectRatioSelect = (scale) => {
    setSelectedScale(scale);
  };

  const uploadImage = async () => {
    setIsImageLoading(true);
    try {
      const options = {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 10,
        useWebWorker: true,
        initialQuality: 0.8,
      };

      const compressedFile = await imageCompression(croppedImage, options);

      const formData = new FormData();
      formData.append("files", compressedFile);
      const response = await EventService.fileUpload(formData);

      setSelectedImage(response?.data?.files?.[0]);
      formik.setFieldValue("image", response?.data?.files?.[0]);
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to upload image");
    } finally {
      setIsImageLoading(false);
    }
  };

  useEffect(() => {
    if (croppedImage) {
      uploadImage();
    }
  }, [croppedImage]);

  return (
    <div className="relative mt-3 h-full min-h-[400px]">
      {globalUsersLoading ? (
        <div className="flex h-full min-h-[75vh] items-center justify-center">
          <img src={Loader} alt="Loading..." className="h-16 w-16" />
        </div>
      ) : (
        <div className="grid h-full grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
            <Card extra={"w-full h-full p-6 overflow-x-auto"}>
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-xl font-bold -mb-2 text-white">Send Push Notification</h4>
                </div>
                <div className="flex flex-col gap-3 text-lg text-white">
                  <p>
                  Notify all users instantly about important updates, announcements, or events across the app.
                  </p>
                  <p className="text-base text-gray-600">
                    • Choose a notification category to organize your message<br />            
                    • Add a short, attention-grabbing title<br />
                    • Write clear and helpful message content<br />
                    • Review carefully before sending to all users
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    className="flex h-[36px] w-[180px] items-center justify-center rounded-md px-5 py-1.5 text-base font-bold bg-[#F44C1D] text-white duration-300"
                    onClick={() => setOpen(true)}
                  >
                    Send Notification
                  </button>
                </div>
              </div>

              {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="mx-3 w-[500px] rounded-xl bg-navy-800 p-6 shadow-lg">
                    <div className="relative flex justify-center py-4">
                      <p className="text-2xl font-semibold text-white">Send New Notification</p>
                      <button
                        className="absolute right-0 cursor-pointer text-2xl text-white"
                        onClick={handleClose}
                      >
                        ×
                      </button>
                    </div>

                    <form onSubmit={formik.handleSubmit}>
                      <div className="mb-3">
                        <label className="mb-2 block text-sm font-medium text-white">
                          Topic
                        </label>
                        <Select
                          options={topicOptions}
                          onChange={handleTopicChange}
                          value={topicOptions.find(option => option.value === formik.values.topic)}
                          className="basic-single"
                          classNamePrefix="select"
                          placeholder="Select a topic"
                          styles={{
                            control: (base) => ({
                              ...base,
                              backgroundColor: 'rgb(17, 25, 40)',
                              borderColor: 'rgb(45, 55, 72)',
                              '&:hover': {
                                borderColor: 'rgb(45, 55, 72)'
                              }
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: 'rgb(17, 25, 40)',
                              color: 'white'
                            }),
                            menuList: (base) => ({
                              ...base,
                              padding: 0
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isFocused ? 'rgb(45, 55, 72)' : 'rgb(17, 25, 40)',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'rgb(45, 55, 72)'
                              }
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color: 'white'
                            })
                          }}
                        />
                        {formik.touched.topic && formik.errors.topic && (
                          <p className="validation-error">{formik.errors.topic}</p>
                        )}
                      </div>

                      <div className="mb-3">
                        <InputField
                          variant="form"
                          extra="mb-2"
                          label="Title"
                          placeholder="Enter notification title"
                          id="title"
                          type="text"
                          name="title"
                          onChange={formik.handleChange}
                          value={formik.values.title}
                        />
                        {formik.touched.title && formik.errors.title && (
                          <p className="validation-error">{formik.errors.title}</p>
                        )}
                      </div>

                      <div className="mb-3">
                        <InputField
                          variant="form"
                          extra="mb-2"
                          label="Body"
                          placeholder="Enter notification body"
                          id="body"
                          type="text"
                          name="body"
                          onChange={formik.handleChange}
                          value={formik.values.body}
                        />
                        {formik.touched.body && formik.errors.body && (
                          <p className="validation-error">{formik.errors.body}</p>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="mb-2 block text-sm font-medium text-white">
                          Image (Optional)
                        </label>
                        <input
                          type="file"
                          hidden
                          id="notification-upload"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                        {isImageLoading ? (
                          <div className="flex min-h-11 w-full items-center justify-center rounded-xl border border-dashed border-white/10">
                            <img
                              src={Loader}
                              alt="Loading..."
                              className="mx-auto h-10 w-10"
                            />
                          </div>
                        ) : (
                          <div className="">
                            {selectedImage ? (
                              <label htmlFor="notification-upload" className="mb-5 mt-1 h-full w-full">
                                <img
                                  src={selectedImage}
                                  className="h-32 w-full cursor-pointer rounded-xl object-cover"
                                  alt="Selected"
                                />
                              </label>
                            ) : (
                              <label
                                htmlFor="notification-upload"
                                className="flex h-full min-h-11 w-full cursor-pointer items-center justify-center rounded-xl border border-dashed border-white/10 duration-300 hover:bg-white/10"
                              >
                                Choose Image
                              </label>
                            )}
                          </div>
                        )}
                        {formik.touched.image && formik.errors.image && (
                          <p className="validation-error">{formik.errors.image}</p>
                        )}
                      </div>

                      {initialImage && (
                        <div className="mb-3">
                          <CroppedImage
                            handleMapClose={() => setInitialImage("")}
                            initialImage={initialImage}
                            setInitialImage={setInitialImage}
                            setSelectedImage={setSelectedImage}
                            setCroppedImage={setCroppedImage}
                            onAspectRatioSelect={handleAspectRatioSelect}
                            selectedScale={selectedScale}
                          />
                        </div>
                      )}

                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="rounded-md bg-orangePrimary px-4 py-2 text-white hover:bg-orange-600 flex items-center gap-2"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <img src={Loader} alt="Loading..." className="h-6 w-6" />
                              Sending...
                            </>
                          ) : (
                            'Send'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </Card>
          </div>
          <UserMessageCard 
            globalUsersList={globalUsersList} 
            globalLoading={globalUsersLoading} 
            globalHasMore={globalHasMore}
            globalPage={globalPage}
            globalLoadingMore={globalUsersLoadingMore}
            fetchMoreGlobalUsers={fetchMoreGlobalUsers}
            globalSettings={globalSettings}
            onRefreshSettings={refreshSettings}
          />
          <MultiUserMessageCard 
            globalUsersList={globalUsersList} 
            globalLoading={globalUsersLoading} 
            globalSettings={globalSettings}
            globalHasMore={globalHasMore}
            globalPage={globalPage}
            globalLoadingMore={globalUsersLoadingMore}
            fetchMoreGlobalUsers={fetchMoreGlobalUsers}
            onRefreshSettings={refreshSettings}
          />
        </div>
      )}
    </div>
  );
};

export default Notification; 